import { EmhEnvironment } from '@dh-io-emh/config-types';
import { CONTEXT_TYPES } from '@dh-io-cscs/user-context-manager/dist/common/constants';
import {
  ConfigService,
  type GetProductPriceCustomerTypeEnum,
  type GetProductPriceTaxProfileEnum,
  Environment as PlsEnvironment,
  PlsService,
  ProductPriceResponseDto,
  RuntimeConfig,
} from '@dh-io-pls/pls-store';
import { Logger } from '../../utils/logger.util';
import { ProductDetail } from '@dh-io-emh/commerce-api/src/models/product';
import Vehicle from '../../models/vehicle.model';
import { WheelOrderEntry } from '../../types/wheels.type';
import { IEquipment } from 'fcis-widget/dist/types/interfaces/ICalculationModel';
import { productDetailToStockProduct } from '../../mappers/pls.mapper';

// Add types here as needed
export type PlsPriceTypes = 'NET_EX_SHOWROOM';

export const getRequestMode = <T extends Vehicle | ProductDetail>(product: T): 'overseas' | 'default' =>
  product.source === 'VX' ? 'overseas' : 'default';

export const getFcisCustomerType = (customerType?: CONTEXT_TYPES): 'business' | 'private' =>
  customerType === CONTEXT_TYPES.B2B ? 'business' : 'private';

export const getPlsEnvironment = (emhEnv: EmhEnvironment): PlsEnvironment =>
  emhEnv.startsWith('dev') ? 'dev' : (emhEnv as PlsEnvironment);

export const getPlsTaxProfile = (customerType?: CONTEXT_TYPES): GetProductPriceTaxProfileEnum =>
  customerType === CONTEXT_TYPES.B2B ? 'NET' : 'GROSS';

export const getPlsCustomerType = (customerType?: CONTEXT_TYPES): GetProductPriceCustomerTypeEnum =>
  customerType === CONTEXT_TYPES.B2B ? 'B2B' : 'B2C';

export const getPlsPriceData = async <T extends Vehicle | ProductDetail>({
  product,
  apiMode,
  shopCountry,
  customerType,
  postCode,
  lang,
  jweToken,
  activeCustomerGroupId,
}: {
  product: T;
  apiMode: EmhEnvironment;
  shopCountry: string;
  lang: string;
  customerType: CONTEXT_TYPES;
  postCode: string | null;
  jweToken: string | null;
  activeCustomerGroupId?: string;
}) => {
  Logger.debug('finance::util', customerType, activeCustomerGroupId);

  const plsConfigService = ConfigService.getInstance();
  const plsService = PlsService.getInstance();
  const plsEnv = getPlsEnvironment(apiMode);
  const plsConfig = (await plsConfigService.getConfig(shopCountry, plsEnv)) as RuntimeConfig;
  const plsApiConfig = plsConfig.pls[plsEnv];

  plsService.init(plsApiConfig);

  return plsService.getPriceData({
    product: productDetailToStockProduct(product),
    market: shopCountry,
    customerType: getPlsCustomerType(customerType),
    taxProfile: getPlsTaxProfile(customerType),
    environment: plsEnv,
    postCode: postCode ?? undefined,
    language: lang,
    customerWebToken: jweToken ?? undefined,
    customerGroup: activeCustomerGroupId,
  });
};

export const findPriceBreakDownItem = (plsData: ProductPriceResponseDto, priceType: PlsPriceTypes) =>
  plsData.priceBreakDown.find((item) => item.type === priceType);

export const getWheelsData = (wheels: WheelOrderEntry[]): IEquipment => {
  if (!wheels?.length) return {};

  const totalPrice = wheels.reduce(
    (subtotal, wheel) => subtotal + (wheel.productPrice?.value || 0) * (wheel.quantity || 1),
    0,
  );

  return {
    code: wheels[0].product?.code,
    codeType: 'NF',
    name: wheels[0].name,
    prices: [
      {
        id: 'grossPrice',
        currency: wheels[0].productPrice?.currencyIso,
        rawValue: totalPrice,
        charges: [],
      },
    ],
  } as IEquipment;
};
